import React, { useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import { useCreateCredential } from '../../hooks/useCredentials';
import { useLanguage } from '../../lang/LangContext';
import { KEYS } from '../../lang/keys';
import { normalizeFieldErrors } from '../../services/api';

const EMPTY_FORM = { title: '', link: '', username: '', password: '' };

const AddCredentialModal = ({ isOpen, onClose, supplierUid }) => {
    const [formData, setFormData] = useState(EMPTY_FORM);
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState('');
    const [showPassword, setShowPassword] = useState(false);

    const createCredential = useCreateCredential(supplierUid);
    const { t } = useLanguage();

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        if (errors[name]) {
            setErrors((prev) => ({ ...prev, [name]: '' }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (formData.title && formData.title.length > 191) {
            newErrors.title = t(KEYS.COMMON_ERROR_MAX_CHARS);
        }

        if (formData.link) {
            if (formData.link.length > 191) {
                newErrors.link = t(KEYS.COMMON_ERROR_MAX_CHARS);
            } else {
                try {
                    new URL(formData.link);
                } catch {
                    newErrors.link = t(KEYS.COMMON_ERROR_INVALID_URL);
                }
            }
        }

        if (formData.username && formData.username.length > 191) {
            newErrors.username = t(KEYS.COMMON_ERROR_MAX_CHARS);
        }

        if (formData.password && formData.password.length > 191) {
            newErrors.password = t(KEYS.COMMON_ERROR_MAX_CHARS);
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitError('');

        if (!validateForm()) return;

        setIsSubmitting(true);
        try {
            const data = {
                title: formData.title || undefined,
                link: formData.link || undefined,
                username: formData.username || undefined,
                password: formData.password || undefined,
            };
            await createCredential.mutateAsync(data);
            setFormData(EMPTY_FORM);
            setShowPassword(false);
            onClose();
        } catch (error) {
            if (error.code === 'VALIDATION_ERROR' && error.errors) {
                setErrors(normalizeFieldErrors(error.errors));
            } else {
                setSubmitError(error.message || t(KEYS.CREDENTIAL_ADD_ERROR));
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        if (!isSubmitting) {
            setFormData(EMPTY_FORM);
            setErrors({});
            setSubmitError('');
            setShowPassword(false);
            onClose();
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            title={t(KEYS.CREDENTIAL_ADD_TITLE)}
            subtitle={t(KEYS.CREDENTIAL_ADD_SUBTITLE)}
            size="medium"
            footer={
                <>
                    <Button variant="outline" size="medium" onClick={handleClose} disabled={isSubmitting}>
                        {t(KEYS.COMMON_CANCEL)}
                    </Button>
                    <Button variant="primary" size="medium" onClick={handleSubmit} disabled={isSubmitting}>
                        {isSubmitting ? t(KEYS.CREDENTIAL_ADD_SUBMITTING) : t(KEYS.CREDENTIAL_ADD_SUBMIT)}
                    </Button>
                </>
            }
        >
            <form onSubmit={handleSubmit} className="p-4 space-y-4">
                {submitError && (
                    <div className="p-3 bg-status-overdue-bg text-status-overdue text-sm rounded-md border border-status-overdue/20">
                        {submitError}
                    </div>
                )}

                {/* Title */}
                <div className="space-y-1">
                    <label className="label-base" htmlFor="cred-title">Title</label>
                    <input
                        id="cred-title"
                        name="title"
                        type="text"
                        value={formData.title}
                        onChange={handleChange}
                        placeholder="e.g. Customer Portal"
                        className={`input-base w-full ${errors.title ? 'border-red-500 focus:ring-red-500' : ''}`}
                    />
                    {errors.title && <span className="text-xs text-red-500 mt-1">{errors.title}</span>}
                </div>

                {/* Link */}
                <div className="space-y-1">
                    <label className="label-base" htmlFor="cred-link">Portal URL</label>
                    <input
                        id="cred-link"
                        name="link"
                        type="text"
                        value={formData.link}
                        onChange={handleChange}
                        placeholder="https://portal.example.com"
                        className={`input-base w-full ${errors.link ? 'border-red-500 focus:ring-red-500' : ''}`}
                    />
                    {errors.link && <span className="text-xs text-red-500 mt-1">{errors.link}</span>}
                </div>

                {/* Username */}
                <div className="space-y-1">
                    <label className="label-base" htmlFor="cred-username">Username</label>
                    <input
                        id="cred-username"
                        name="username"
                        type="text"
                        value={formData.username}
                        onChange={handleChange}
                        placeholder="user@example.com"
                        autoComplete="off"
                        className={`input-base w-full ${errors.username ? 'border-red-500 focus:ring-red-500' : ''}`}
                    />
                    {errors.username && <span className="text-xs text-red-500 mt-1">{errors.username}</span>}
                </div>

                {/* Password */}
                <div className="space-y-1">
                    <label className="label-base" htmlFor="cred-password">Password</label>
                    <div className="relative">
                        <input
                            id="cred-password"
                            name="password"
                            type={showPassword ? 'text' : 'password'}
                            value={formData.password}
                            onChange={handleChange}
                            placeholder="••••••••"
                            autoComplete="new-password"
                            className={`input-base w-full pr-10 ${errors.password ? 'border-red-500 focus:ring-red-500' : ''}`}
                        />
                        <button
                            type="button"
                            onClick={() => setShowPassword((v) => !v)}
                            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-text-muted hover:text-accent transition-colors"
                        >
                            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                    </div>
                    {errors.password && <span className="text-xs text-red-500 mt-1">{errors.password}</span>}
                </div>
            </form>
        </Modal>
    );
};

export default AddCredentialModal;
